import type { TextareaEdit } from '../types/textarea-edit'

export function commonPrefixLength(a: string, b: string): number {
  const max = Math.min(a.length, b.length)
  let i = 0

  while (i < max && a[i] === b[i]) i++

  return i
}

/**
 * Reconstruct an edit from the common affixes alone — the caret-blind form.
 *
 * Byte-identical to the diff `handleTextChange` used to inline. Kept as the fallback for the two
 * cases `resolveTextareaEdit` cannot anchor (a non-collapsed post-edit selection, an in-flight IME
 * composition) and as its self-check's escape hatch. It always reproduces `after` exactly, but it
 * cannot say WHERE among several equally valid edits the user actually typed: the prefix scan runs
 * first, so an ambiguity is resolved as far RIGHT as the repeated characters allow.
 */
export function affixTextareaEdit(before: string, after: string): TextareaEdit {
  const prefix = commonPrefixLength(before, after)
  let suffix = 0

  while (
    suffix < before.length - prefix &&
    suffix < after.length - prefix &&
    before[before.length - 1 - suffix] === after[after.length - 1 - suffix]
  )
    suffix++

  return {
    deleteStart: prefix,
    deleteEnd: before.length - suffix,
    insertText: after.slice(prefix, after.length - suffix),
  }
}

/**
 * Reconstruct the edit a textarea just applied, **anchored to the caret it left behind**.
 *
 * `affixTextareaEdit`'s rightward bias is what deleted the wrong character in a linked chain: with
 * the caret mid-frame and the text from there to the frame's end all one repeated character (a run
 * of spaces at a wrapped seam, a doubled letter), `after` becomes a strict PREFIX of `before`, the
 * suffix scan matches nothing, and the reconstructed range is `[before.length - 1, before.length)`
 * — "delete the final character". The character the user meant to delete survives and one they
 * never touched is destroyed.
 *
 * A single-caret edit stops being ambiguous once the caret is known, because the replaced region
 * ENDS at the caret by definition:
 *
 * ```
 * changeEndNew = caretAfter
 * changeEndOld = caretAfter - (after.length - before.length)
 * deleteStart  = min(commonPrefix, changeEndNew, changeEndOld)
 * ```
 *
 * The `min` is the whole fix — it stops the prefix scan walking past the caret into the repeated
 * run. Where the affix diff was already unambiguous the two agree exactly, so ordinary typing is
 * bit-for-bit unchanged. Checked by hand against every shape this surface produces: mid-text
 * Backspace `[k-1, k)`, Delete `[k, k+1)`, insert at the caret, Backspace over a range `[s, e)`,
 * and select-all-then-type.
 *
 * **It verifies its own premise instead of assuming it.** The formula holds only if `caretAfter` is
 * the POST-edit caret. That is what the `input` event guarantees — and what already makes the
 * painted caret land correctly on every keystroke in the app, since a pre-edit reading would paint
 * one character behind everywhere — but it is load-bearing enough not to take on trust: the
 * reconstruction is replayed against `before`, and anything that fails to reproduce `after`
 * exactly falls back to the affix diff. So this can never be worse than the caret-blind form,
 * whatever the browser does with the selection.
 */
export function resolveTextareaEdit(
  before: string,
  after: string,
  caretAfter: number | null
): TextareaEdit {
  if (caretAfter != null) {
    const changeEndNew = caretAfter
    const changeEndOld = caretAfter - (after.length - before.length)

    if (
      changeEndNew >= 0 &&
      changeEndNew <= after.length &&
      changeEndOld >= 0 &&
      changeEndOld <= before.length
    ) {
      const deleteStart = Math.min(commonPrefixLength(before, after), changeEndNew, changeEndOld)
      const insertText = after.slice(deleteStart, changeEndNew)

      if (before.slice(0, deleteStart) + insertText + before.slice(changeEndOld) === after) {
        return { deleteStart, deleteEnd: changeEndOld, insertText }
      }
    }
  }

  return affixTextareaEdit(before, after)
}
